import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, MONTHS_ES, formatMoney } from "../lib/api";
import {
  Plus, FloppyDisk, ArrowLeft, Trash, Pencil, FileCsv, FilePdf, FileXls,
  Clock, GitCommit, GitMerge, TrendUp, TrendDown, Wallet,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { SaveAsTemplateButton } from "../components/SaveAsTemplateButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Legend,
} from "recharts";

const emptyEntry = () => ({
  id: null,
  type: "income",
  category: "",
  subcategory: "",
  description: "",
  amount: 0,
  expected_date: new Date().toISOString().slice(0, 10),
  comments: "",
});

export default function PlanDetail() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [version, setVersion] = useState(null);
  const [categories, setCategories] = useState([]);
  const [entries, setEntries] = useState([]);
  const [initialBalance, setInitialBalance] = useState(0);
  const [editing, setEditing] = useState(null);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveMeta, setSaveMeta] = useState({ change_description: "" });
  const [dirty, setDirty] = useState(false);
  const [viewingVersion, setViewingVersion] = useState(null);

  const load = async (versionNumber = null) => {
    try {
      const planData = await api.getPlan(planId);
      setPlan(planData.plan);
      const vn = versionNumber ?? planData.plan.current_version_number;
      const vData = await api.getVersion(planId, vn);
      setVersion(vData.version);
      setEntries(vData.version.entries || []);
      setInitialBalance(vData.version.initial_balance);
      setViewingVersion(vn);
      setDirty(false);
    } catch (e) {
      toast.error("Error al cargar el plan");
    }
  };

  useEffect(() => {
    load();
    api.listCategories().then(setCategories);
  }, [planId]);

  const isViewingCurrent = plan && viewingVersion === plan.current_version_number;
  const isEditable = isViewingCurrent;

  const totals = useMemo(() => {
    const income = entries.filter((e) => e.type === "income").reduce((s, e) => s + Number(e.amount), 0);
    const expense = entries.filter((e) => e.type === "expense").reduce((s, e) => s + Number(e.amount), 0);
    return {
      total_income: income,
      total_expense: expense,
      net: income - expense,
      final: Number(initialBalance) + income - expense,
    };
  }, [entries, initialBalance]);

  const handleSaveEntry = (entry) => {
    const normalized = {
      ...entry,
      amount: parseFloat(entry.amount) || 0,
      id: entry.id || `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    };
    setEntries((prev) => {
      const idx = prev.findIndex((e) => e.id === normalized.id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = normalized;
        return copy;
      }
      return [...prev, normalized];
    });
    setDirty(true);
    setEntryDialogOpen(false);
    setEditing(null);
  };

  const handleDeleteEntry = (id) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
    setDirty(true);
  };

  const handleSaveVersion = async () => {
    if (!saveMeta.change_description.trim()) {
      toast.error("Describe el motivo del cambio");
      return;
    }
    try {
      const cleanEntries = entries.map((e) => ({
        ...e,
        id: e.id?.startsWith?.("local-") ? undefined : e.id,
      }));
      const result = await api.createVersion(planId, {
        change_description: saveMeta.change_description,
        initial_balance: Number(initialBalance),
        entries: cleanEntries,
      });
      toast.success(`V${result.version.version_number} guardada`);
      setSaveDialogOpen(false);
      setSaveMeta({ change_description: "" });
      load(result.version.version_number);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar versión");
    }
  };

  const chartData = useMemo(() => {
    // Build daily cumulative chart ordered by expected_date
    const sorted = [...entries].sort((a, b) => (a.expected_date || "").localeCompare(b.expected_date || ""));
    let bal = Number(initialBalance);
    const pts = [{ date: "Inicio", balance: bal, income: 0, expense: 0 }];
    sorted.forEach((e) => {
      const amt = Number(e.amount) || 0;
      if (e.type === "income") bal += amt;
      else bal -= amt;
      pts.push({
        date: e.expected_date || "—",
        balance: bal,
        income: e.type === "income" ? amt : 0,
        expense: e.type === "expense" ? amt : 0,
      });
    });
    return pts;
  }, [entries, initialBalance]);

  const categoryBreakdown = useMemo(() => {
    const map = {};
    entries.forEach((e) => {
      if (!map[e.category]) map[e.category] = { category: e.category || "Sin categoría", income: 0, expense: 0 };
      map[e.category][e.type] += Number(e.amount) || 0;
    });
    return Object.values(map);
  }, [entries]);

  if (!plan) return <div className="p-6">Cargando…</div>;

  return (
    <div>
      {/* Top bar */}
      <div className="border-b border-border bg-card px-6 py-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/")} data-testid="back-to-dashboard-btn" className="p-2 rounded-sm hover:bg-muted">
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="label-xs text-muted-foreground">
              {MONTHS_ES[plan.month]} · {plan.year}
            </div>
            <h1 className="font-heading text-xl font-extrabold tracking-tight">{plan.name}</h1>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Select value={String(viewingVersion)} onValueChange={(v) => load(parseInt(v))}>
            <SelectTrigger data-testid="version-selector" className="w-40 rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <VersionsOptions planId={planId} currentVersion={plan.current_version_number} />
            </SelectContent>
          </Select>

          <Link to={`/plans/${planId}/versions`}>
            <Button data-testid="view-versions-btn" variant="outline" className="rounded-sm">
              <Clock size={16} className="mr-2" weight="bold" /> Historial
            </Button>
          </Link>
          <Link to={`/plans/${planId}/compare`}>
            <Button data-testid="compare-versions-btn" variant="outline" className="rounded-sm">
              <GitMerge size={16} className="mr-2" weight="bold" /> Comparar
            </Button>
          </Link>

          <SaveAsTemplateButton planId={planId} versionNumber={viewingVersion} defaultName={plan.name} dirty={dirty} />

          <ExportMenu planId={planId} versionNumber={viewingVersion} />

          {isEditable && (
            <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="save-version-btn" disabled={!dirty} className="rounded-sm font-semibold">
                  <FloppyDisk size={16} className="mr-2" weight="bold" />
                  Guardar Versión
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="save-version-dialog" className="rounded-sm max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl font-extrabold tracking-tight">
                    Crear V{plan.current_version_number + 1}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <Label className="label-xs text-muted-foreground">Motivo del cambio</Label>
                    <Textarea
                      data-testid="save-change-description"
                      rows={3}
                      value={saveMeta.change_description}
                      onChange={(e) => setSaveMeta({ ...saveMeta, change_description: e.target.value })}
                      placeholder="Ej: Actualización de proyección de ventas Q1"
                      className="mt-1 rounded-sm"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">El autor se registra automáticamente con tu cuenta.</p>
                </div>
                <DialogFooter>
                  <Button data-testid="confirm-save-version-btn" onClick={handleSaveVersion} className="rounded-sm font-semibold">
                    Guardar como V{plan.current_version_number + 1}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {!isEditable && (
        <div className="bg-amber-50 border-b border-amber-200 dark:bg-amber-950 dark:border-amber-800 px-6 py-3 text-sm">
          <span className="label-xs text-amber-700 dark:text-amber-300">Modo lectura</span>
          <span className="ml-3 text-amber-900 dark:text-amber-200">
            Estás viendo V{viewingVersion}. Vuelve a la versión vigente (V{plan.current_version_number}) para editar.
          </span>
        </div>
      )}

      <div className="p-6 space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <SummaryCard
            label="Saldo inicial"
            value={formatMoney(Number(initialBalance))}
            icon={Wallet}
            editable={isEditable}
            onEdit={isEditable ? (v) => { setInitialBalance(v); setDirty(true); } : null}
            rawValue={initialBalance}
          />
          <SummaryCard
            label="Ingresos proyectados"
            value={formatMoney(totals.total_income)}
            icon={TrendUp}
            accent="emerald"
          />
          <SummaryCard
            label="Egresos proyectados"
            value={formatMoney(totals.total_expense)}
            icon={TrendDown}
            accent="red"
          />
          <SummaryCard
            label="Saldo final proyectado"
            value={formatMoney(totals.final)}
            icon={Wallet}
            accent={totals.final >= 0 ? "emerald" : "red"}
            highlight
          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="card-elevated p-5 lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-sm">EVOLUCIÓN DE SALDO</h3>
              <span className="label-xs text-muted-foreground">Proyectado al cierre</span>
            </div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="balGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" fontSize={10} stroke="hsl(var(--muted-foreground))" />
                  <YAxis fontSize={10} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ fontFamily: "var(--font-body)", fontSize: 12, borderRadius: 4 }}
                    formatter={(v) => formatMoney(v)}
                  />
                  <Area type="monotone" dataKey="balance" stroke="hsl(var(--chart-1))" fill="url(#balGradient)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="card-elevated p-5">
            <h3 className="font-heading font-bold text-sm mb-3">POR CATEGORÍA</h3>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={categoryBreakdown} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" fontSize={10} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <YAxis dataKey="category" type="category" fontSize={10} width={80} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => formatMoney(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="income" name="Ingresos" fill="hsl(161 94% 35%)" />
                  <Bar dataKey="expense" name="Egresos" fill="hsl(0 84% 55%)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Entries table */}
        <div className="card-elevated">
          <div className="p-5 flex items-center justify-between border-b border-border">
            <div>
              <h3 className="font-heading font-bold">Partidas</h3>
              <p className="text-xs text-muted-foreground mt-1">
                {entries.length} partidas registradas
              </p>
            </div>
            {isEditable && (
              <Button
                data-testid="add-entry-btn"
                onClick={() => { setEditing(emptyEntry()); setEntryDialogOpen(true); }}
                className="rounded-sm font-semibold"
              >
                <Plus size={16} className="mr-2" weight="bold" /> Nueva Partida
              </Button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm table-row-hover">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left px-4 py-2 label-xs text-muted-foreground">Tipo</th>
                  <th className="text-left px-4 py-2 label-xs text-muted-foreground">Categoría</th>
                  <th className="text-left px-4 py-2 label-xs text-muted-foreground">Descripción</th>
                  <th className="text-right px-4 py-2 label-xs text-muted-foreground">Monto</th>
                  <th className="text-left px-4 py-2 label-xs text-muted-foreground">Fecha</th>
                  <th className="text-left px-4 py-2 label-xs text-muted-foreground">Comentarios</th>
                  {isEditable && <th className="px-4 py-2" />}
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && (
                  <tr>
                    <td colSpan={isEditable ? 7 : 6} className="text-center text-muted-foreground py-10">
                      No hay partidas. {isEditable && "Agrega una para comenzar."}
                    </td>
                  </tr>
                )}
                {entries.map((e) => (
                  <tr key={e.id} className="border-b border-border" data-testid={`entry-row-${e.id}`}>
                    <td className="px-4 py-3">
                      <span className={`label-xs px-2 py-0.5 rounded-sm ${e.type === "income" ? "income-chip" : "expense-chip"}`}>
                        {e.type === "income" ? "Ingreso" : "Egreso"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{e.category || "—"}</div>
                      {e.subcategory && (
                        <div className="text-xs text-muted-foreground">{e.subcategory}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">{e.description}</td>
                    <td className={`px-4 py-3 text-right font-mono-tabular font-semibold ${e.type === "income" ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400"}`}>
                      {e.type === "expense" && "-"}{formatMoney(Number(e.amount))}
                    </td>
                    <td className="px-4 py-3 font-mono-tabular text-xs">{e.expected_date}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground max-w-xs truncate">{e.comments}</td>
                    {isEditable && (
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          data-testid={`edit-entry-${e.id}`}
                          onClick={() => { setEditing(e); setEntryDialogOpen(true); }}
                          className="p-1.5 hover:bg-muted rounded-sm text-muted-foreground hover:text-foreground"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          data-testid={`delete-entry-${e.id}`}
                          onClick={() => handleDeleteEntry(e.id)}
                          className="p-1.5 hover:bg-muted rounded-sm text-muted-foreground hover:text-destructive ml-1"
                        >
                          <Trash size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Entry dialog */}
      <EntryDialog
        open={entryDialogOpen}
        onOpenChange={setEntryDialogOpen}
        entry={editing}
        setEntry={setEditing}
        categories={categories}
        onSave={handleSaveEntry}
      />
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, accent, highlight, editable, onEdit, rawValue }) {
  const [editMode, setEditMode] = useState(false);
  const [val, setVal] = useState(rawValue);

  useEffect(() => { setVal(rawValue); }, [rawValue]);

  const accentCls =
    accent === "emerald" ? "text-emerald-600 dark:text-emerald-400" :
    accent === "red" ? "text-red-600 dark:text-red-400" :
    "";

  return (
    <div className={`card-elevated p-5 ${highlight ? "border-2 border-foreground" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="label-xs text-muted-foreground">{label}</div>
        <Icon size={16} weight="duotone" className={accentCls} />
      </div>
      {editMode && editable ? (
        <div className="mt-2 flex items-center gap-2">
          <Input
            type="number"
            step="0.01"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            className="rounded-sm font-mono-tabular"
            data-testid="initial-balance-edit-input"
          />
          <Button
            size="sm"
            data-testid="save-initial-balance-btn"
            onClick={() => { onEdit(parseFloat(val) || 0); setEditMode(false); }}
            className="rounded-sm"
          >
            OK
          </Button>
        </div>
      ) : (
        <div className="flex items-end justify-between mt-1">
          <div className={`font-heading font-black text-2xl font-mono-tabular tracking-tight ${accentCls}`}>
            {value}
          </div>
          {editable && (
            <button
              data-testid="edit-initial-balance-btn"
              onClick={() => setEditMode(true)}
              className="p-1 rounded-sm hover:bg-muted text-muted-foreground"
            >
              <Pencil size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function VersionsOptions({ planId, currentVersion }) {
  const [versions, setVersions] = useState([]);
  useEffect(() => {
    api.listVersions(planId).then((d) => setVersions(d.versions || []));
  }, [planId]);
  return (
    <>
      {versions.map((v) => (
        <SelectItem key={v.version_number} value={String(v.version_number)}>
          V{v.version_number}{v.version_number === currentVersion ? " · Vigente" : ""}
        </SelectItem>
      ))}
    </>
  );
}

function ExportMenu({ planId, versionNumber }) {
  return (
    <div className="flex items-center gap-1">
      <a href={api.exportUrl(planId, versionNumber, "csv")} download>
        <Button data-testid="export-csv-btn" variant="outline" className="rounded-sm" title="Exportar CSV">
          <FileCsv size={16} weight="bold" />
        </Button>
      </a>
      <a href={api.exportUrl(planId, versionNumber, "xlsx")} download>
        <Button data-testid="export-xlsx-btn" variant="outline" className="rounded-sm" title="Exportar Excel">
          <FileXls size={16} weight="bold" />
        </Button>
      </a>
      <a href={api.exportUrl(planId, versionNumber, "pdf")} download>
        <Button data-testid="export-pdf-btn" variant="outline" className="rounded-sm" title="Exportar PDF">
          <FilePdf size={16} weight="bold" />
        </Button>
      </a>
    </div>
  );
}

function EntryDialog({ open, onOpenChange, entry, setEntry, categories, onSave }) {
  if (!entry) return null;
  const subcategoriesForCurrent =
    categories.find((c) => c.name === entry.category && c.type === entry.type)?.subcategories || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="entry-dialog" className="rounded-sm max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-extrabold tracking-tight">
            {entry.id ? "Editar Partida" : "Nueva Partida"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div>
            <Label className="label-xs text-muted-foreground">Tipo</Label>
            <Select value={entry.type} onValueChange={(v) => setEntry({ ...entry, type: v, category: "", subcategory: "" })}>
              <SelectTrigger data-testid="entry-type-select" className="mt-1 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="income">Ingreso</SelectItem>
                <SelectItem value="expense">Egreso</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-xs text-muted-foreground">Monto (USD)</Label>
            <Input
              data-testid="entry-amount-input"
              type="number" step="0.01"
              value={entry.amount}
              onChange={(e) => setEntry({ ...entry, amount: e.target.value })}
              className="mt-1 rounded-sm font-mono-tabular"
            />
          </div>
          <div>
            <Label className="label-xs text-muted-foreground">Categoría</Label>
            <Select value={entry.category} onValueChange={(v) => setEntry({ ...entry, category: v, subcategory: "" })}>
              <SelectTrigger data-testid="entry-category-select" className="mt-1 rounded-sm">
                <SelectValue placeholder="Selecciona…" />
              </SelectTrigger>
              <SelectContent>
                {categories
                  .filter((c) => c.type === entry.type)
                  .map((c) => (
                    <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-xs text-muted-foreground">Subcategoría</Label>
            <Select value={entry.subcategory || ""} onValueChange={(v) => setEntry({ ...entry, subcategory: v })}>
              <SelectTrigger data-testid="entry-subcategory-select" className="mt-1 rounded-sm">
                <SelectValue placeholder="Opcional" />
              </SelectTrigger>
              <SelectContent>
                {subcategoriesForCurrent.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="label-xs text-muted-foreground">Descripción</Label>
            <Input
              data-testid="entry-description-input"
              value={entry.description}
              onChange={(e) => setEntry({ ...entry, description: e.target.value })}
              className="mt-1 rounded-sm"
            />
          </div>
          <div>
            <Label className="label-xs text-muted-foreground">Fecha esperada</Label>
            <Input
              data-testid="entry-date-input"
              type="date"
              value={entry.expected_date}
              onChange={(e) => setEntry({ ...entry, expected_date: e.target.value })}
              className="mt-1 rounded-sm"
            />
          </div>
          <div className="col-span-2">
            <Label className="label-xs text-muted-foreground">Comentarios</Label>
            <Textarea
              data-testid="entry-comments-input"
              rows={2}
              value={entry.comments || ""}
              onChange={(e) => setEntry({ ...entry, comments: e.target.value })}
              className="mt-1 rounded-sm"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            data-testid="save-entry-btn"
            onClick={() => {
              if (!entry.description.trim()) { toast.error("Agrega una descripción"); return; }
              if (!entry.category) { toast.error("Selecciona una categoría"); return; }
              onSave(entry);
            }}
            className="rounded-sm font-semibold"
          >
            <GitCommit size={16} className="mr-2" /> Guardar Partida
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
